/**
 * Um mapa ação → tecla. Nenhum componente registra keydown global:
 * liga a ação, empilha o fechar, e o ouvinte daqui resolve.
 *
 * Grava `code` (posição física); mostra o que está impresso na tampa.
 * Persistido em localStorage — só o que difere do padrão.
 */

import { arrastandoItem } from './arraste';

export const ACOES = [
  'andarNorte', 'andarSul', 'andarLeste', 'andarOeste',
  'galopar', 'ciclarAlvo', 'alvoGrupo1', 'alvoGrupo2', 'alvoGrupo3', 'limparAlvo', 'fechar', 'atacar', 'cancelarMagia', 'autoAlvo',
  'conjurar1', 'conjurar2', 'conjurar3', 'conjurar4', 'conjurar5', 'conjurar6',
  'conjurar7', 'conjurar8', 'conjurar9', 'conjurar10',
  'conjurar11', 'conjurar12', 'conjurar13', 'conjurar14', 'conjurar15', 'conjurar16',
  'perfil', 'mapa', 'ordens', 'mochila', 'mercado',
  'guilda', 'amigos', 'gm', 'config', 'cacar', 'desempenho',
  'viajar', 'lojas', 'banco', 'oficios', 'falar'
] as const;

export type Acao = typeof ACOES[number];

const MANTIDAS: ReadonlySet<Acao> = new Set([
  'andarNorte', 'andarSul', 'andarLeste', 'andarOeste'
]);

/** Um toque liga, o seguinte desliga. Soltar a tecla não cancela. */
const ALTERNADAS: ReadonlySet<Acao> = new Set(['galopar']);

/**
 * Segurada sem fazer nada sozinha: só muda o que a outra tecla faz. O Self cast (V + tecla da magia) lança
 * cura, cura de veneno e buff em você mesmo, com ou sem alvo marcado.
 */
const SEGURADAS: ReadonlySet<Acao> = new Set(['autoAlvo']);

const FIXAS: ReadonlySet<Acao> = new Set(['limparAlvo', 'fechar']);

/**
 * Só com o foco no mundo: Enter num botão, numa linha do grimório ou numa aba continua sendo
 * o Enter da página (escolher, confirmar).
 */
const SO_NO_MUNDO: ReadonlySet<Acao> = new Set(['falar']);

function focoNoMundo(e: KeyboardEvent): boolean {
  const alvo = e.target;
  if (!(alvo instanceof Element) || alvo === document.body || alvo === document.documentElement) return true;
  if (alvo instanceof HTMLCanvasElement) return true;
  return !alvo.closest('button, a[href], select, summary, [tabindex]:not([tabindex="-1"]), [role="button"], [role="option"], [role="menuitem"], [role="tab"], [role="radio"], [role="checkbox"]');
}

/**
 * Espaço + F1–F8 e, para os slots 9 a 16 (a barra tem de 8 a 16, Configurações › Interface, 28/10/2026), a fileira
 * 1–8. A tecla de um slot escondido não dispara nada.
 */
export const ACOES_DA_BARRA = [
  'atacar',
  'conjurar1', 'conjurar2', 'conjurar3', 'conjurar4', 'conjurar5', 'conjurar6',
  'conjurar7', 'conjurar8', 'conjurar9', 'conjurar10', 'conjurar11', 'conjurar12',
  'conjurar13', 'conjurar14', 'conjurar15', 'conjurar16'
] as const satisfies readonly Acao[];

const SLOTS: ReadonlySet<Acao> = new Set(ACOES_DA_BARRA.filter(a => a !== 'atacar'));

/** Atalhos da barra: passam por gump e modal. Campo de texto ainda engole. */
function daBarra(acao: Acao): boolean {
  return acao === 'atacar' || acao === 'cancelarMagia' || SLOTS.has(acao);
}

/** Segunda tecla do par fixo do movimento — não entra no arquivo salvo. */
const SETA: Partial<Record<Acao, string>> = {
  andarNorte: 'ArrowUp',
  andarSul: 'ArrowDown',
  andarOeste: 'ArrowLeft',
  andarLeste: 'ArrowRight'
};

export const PADRAO: Readonly<Record<Acao, string>> = {
  andarNorte: 'KeyW',
  andarSul: 'KeyS',
  andarOeste: 'KeyA',
  andarLeste: 'KeyD',
  galopar: 'Shift',
  ciclarAlvo: 'Tab',
  // Placas do grupo (techspec-grupo-placas): marcam o 1º, 2º e 3º companheiro. No Linux o navegador usa Alt+1…9 para as abas.
  alvoGrupo1: 'Alt+Digit1',
  alvoGrupo2: 'Alt+Digit2',
  alvoGrupo3: 'Alt+Digit3',
  limparAlvo: 'Escape',
  fechar: 'Escape',
  atacar: 'Space',
  // O V é do Self cast (28/10/2026). O antigo "Sair do War" saiu: parar de atacar é clicar no slot da arma.
  cancelarMagia: 'KeyX',
  autoAlvo: 'KeyV',
  conjurar1: 'F1',
  conjurar2: 'F2',
  conjurar3: 'F3',
  conjurar4: 'F4',
  conjurar5: 'F5',
  conjurar6: 'F6',
  conjurar7: 'F7',
  conjurar8: 'F8',
  // Slots 9 a 16 (a barra pode ter até 16, Configurações › Interface): a fileira dos números.
  conjurar9: 'Digit1',
  conjurar10: 'Digit2',
  conjurar11: 'Digit3',
  conjurar12: 'Digit4',
  conjurar13: 'Digit5',
  conjurar14: 'Digit6',
  conjurar15: 'Digit7',
  conjurar16: 'Digit8',
  perfil: 'KeyP',
  mapa: 'KeyM',
  ordens: 'KeyO',
  mochila: 'KeyI',
  mercado: 'KeyB',
  guilda: 'KeyG',
  amigos: 'KeyF',
  gm: 'Alt+KeyG',
  config: 'KeyC',
  cacar: 'KeyH',
  desempenho: 'F9',
  viajar: 'KeyT',
  lojas: 'KeyL',
  banco: 'KeyK',
  oficios: 'KeyU',
  falar: 'Enter'
};

export const GRUPOS: ReadonlyArray<{ chave: string; acoes: readonly Acao[] }> = [
  {
    chave: 'atalho.grupo.movimento',
    acoes: ['andarNorte', 'andarSul', 'andarOeste', 'andarLeste', 'galopar', 'ciclarAlvo', 'alvoGrupo1', 'alvoGrupo2', 'alvoGrupo3', 'limparAlvo']
  },
  {
    chave: 'atalho.grupo.combate',
    acoes: ['atacar', 'cancelarMagia', 'autoAlvo']
  },
  {
    chave: 'atalho.grupo.barra',
    acoes: ACOES_DA_BARRA
  },
  {
    chave: 'atalho.grupo.gumps',
    acoes: ['falar', 'mochila', 'perfil', 'cacar', 'mapa', 'mercado', 'guilda', 'amigos', 'config', 'gm', 'desempenho', 'viajar', 'lojas', 'banco', 'fechar']
  }
];

const KEY = 'uoidle.atalhos';
const VERSAO = 2;

/** Ações que ganharam tecla nesta leva. O selo some depois de NOVAS_ATE. */
export const ACOES_NOVAS: ReadonlySet<Acao> = new Set(['viajar', 'lojas', 'banco', 'falar', 'cancelarMagia', 'autoAlvo', 'alvoGrupo1', 'alvoGrupo2', 'alvoGrupo3']);
/** Ajustar para a data do deploy + 30 dias. */
export const NOVAS_ATE = '2026-12-31';
export const mostrarNovas = Date.now() < Date.parse(NOVAS_ATE);

const NAVEGADOR = new Set(['Ctrl+KeyW', 'Ctrl+KeyT', 'Ctrl+Shift+KeyI', 'Alt+F4']);

/** Mundo montado: F1–F12 e atalhos de Chrome não vazam para o browser. */
let noMundo = false;

export function marcarMundo(ativo: boolean) {
  noMundo = ativo;
}

function ehTeclaFuncao(code: string): boolean {
  return /^F([1-9]|1[0-2])$/.test(code);
}

function teclaDoChrome(e: KeyboardEvent): boolean {
  const code = e.code;
  if (ehTeclaFuncao(code)) return true;
  if (e.altKey && !e.ctrlKey && !e.metaKey) {
    return code === 'F4' || code === 'ArrowLeft' || code === 'ArrowRight'
      || code === 'Home' || code === 'KeyD';
  }
  const cmd = e.ctrlKey || e.metaKey;
  if (!cmd) return false;
  if (e.shiftKey) {
    return code === 'KeyI' || code === 'KeyJ' || code === 'KeyC' || code === 'KeyN'
      || code === 'KeyT' || code === 'KeyW' || code === 'KeyR' || code === 'Delete'
      || code === 'KeyP';
  }
  return code === 'KeyN' || code === 'KeyT' || code === 'KeyW' || code === 'KeyR'
    || code === 'KeyL' || code === 'KeyP' || code === 'KeyS' || code === 'KeyU'
    || code === 'KeyO' || code === 'KeyD' || code === 'KeyH' || code === 'KeyJ'
    || code === 'KeyF' || code === 'KeyG' || code === 'KeyK' || code === 'KeyE'
    || code === 'KeyA' || code === 'Digit0' || code === 'Minus' || code === 'Equal'
    || code === 'NumpadAdd' || code === 'NumpadSubtract'
    || code === 'Tab' || code === 'PageUp' || code === 'PageDown';
}

function engolirChrome(e: KeyboardEvent) {
  e.preventDefault();
  e.stopPropagation();
  if (ehTeclaFuncao(e.code)) e.stopImmediatePropagation();
}

const mapa: Record<Acao, string> = { ...PADRAO };
// Corrida ligada por padrão, a pé ou montado (Renato, 28/10/2026). Shift alterna para andar e de volta.
const pressionadas = new Set<Acao>(['galopar']);
const toques = new Map<Acao, () => void>();
const ouvintes = new Set<() => void>();

let geracao = 0;
let ligado = false;
let layout: { get(code: string): string | undefined } | null = null;
let aoAndar: (() => void) | null = null;

export interface CamadaFechar {
  fechar(): void;
  /** Overlay: só esta camada e o que ela deixar passar. */
  modal: boolean;
  deixaPassar: ReadonlySet<Acao>;
  /** Reservado pelas janelas; a barra (atalhos e Espaço) ignora. */
  comeSlots: boolean;
}

const pilha: CamadaFechar[] = [];

export interface MiraTeclado {
  ativa(): boolean;
  cancelar(): void;
}

let mira: MiraTeclado | null = null;

export type RecusaCaptura = 'modificador' | 'navegador';

export type ResultadoCaptura =
  | { ok: true; tecla: string }
  | { ok: false; motivo: 'cancelou' }
  | { ok: false; motivo: RecusaCaptura };

let captura: { acao: Acao; avisar: (r: ResultadoCaptura) => void } | null = null;

function ehAcao(raw: unknown): raw is Acao {
  return typeof raw === 'string' && (ACOES as readonly string[]).includes(raw);
}

function teclaLegivel(raw: unknown): raw is string {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 48) return false;
  const tecla = normalizarTecla(raw);
  if (tecla === 'Shift' || tecla === 'Control' || tecla === 'Alt' || tecla === 'Meta') return true;
  const partes = tecla.split('+');
  const code = partes.pop();
  if (!code || !/^[A-Za-z0-9]+$/.test(code)) return false;
  for (const p of partes) {
    if (p !== 'Ctrl' && p !== 'Alt' && p !== 'Shift' && p !== 'Meta') return false;
  }
  return true;
}

/** `1` gravado na mão vira `Digit1` — o ouvinte só vê `e.code`. */
function normalizarTecla(raw: string): string {
  const partes = raw.split('+');
  const code = partes.pop() ?? raw;
  const canon = code === ' ' || code === 'Spacebar' ? 'Space'
    : code === 'Esc' ? 'Escape'
    : /^[0-9]$/.test(code) ? `Digit${code}`
    : code;
  return partes.length ? `${partes.join('+')}+${canon}` : canon;
}

function readStored() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { versao?: unknown; mapa?: unknown };
    if (parsed.versao !== VERSAO || parsed.mapa == null || typeof parsed.mapa !== 'object') return;
    const gravado = parsed.mapa as Record<string, unknown>;
    for (const [k, v] of Object.entries(gravado)) {
      if (!ehAcao(k) || FIXAS.has(k) || !teclaLegivel(v)) continue;
      mapa[k] = normalizarTecla(v);
    }
  } catch {
    /* storage cheio ou JSON ruim — fica o padrão */
  }
}

readStored();

function persist() {
  const diffs: Record<string, string> = {};
  for (const acao of ACOES) {
    if (FIXAS.has(acao)) continue;
    if (mapa[acao] !== PADRAO[acao]) diffs[acao] = mapa[acao];
  }
  try {
    if (Object.keys(diffs).length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify({ versao: VERSAO, mapa: diffs }));
  } catch {
    /* ignore */
  }
}

function avisar() {
  geracao += 1;
  for (const fn of ouvintes) fn();
}

export function assinarAtalhos(fn: () => void): () => void {
  ouvintes.add(fn);
  return () => { ouvintes.delete(fn); };
}

export function geracaoAtalhos(): number {
  return geracao;
}

export function teclaDe(acao: Acao): string {
  return mapa[acao];
}

export function teclasDe(acao: Acao): readonly string[] {
  const primaria = mapa[acao];
  const extra = SETA[acao];
  return extra && extra !== primaria ? [primaria, extra] : [primaria];
}

export function acaoFixa(acao: Acao): boolean {
  return FIXAS.has(acao);
}

export function snapshotAtalhos(): Readonly<Record<Acao, string>> {
  return { ...mapa };
}

export function aplicarAtalho(acao: Acao, tecla: string) {
  if (!ehAcao(acao) || FIXAS.has(acao) || !teclaLegivel(tecla)) return;
  mapa[acao] = normalizarTecla(tecla);
  persist();
  avisar();
}

export function restaurarAtalhos() {
  for (const acao of ACOES) mapa[acao] = PADRAO[acao];
  persist();
  avisar();
}

export function pressionada(acao: Acao): boolean {
  return pressionadas.has(acao);
}

/** Corrida ligada/desligada pelo clique no ícone da barra (o mesmo que apertar o Shift). */
export function alternarCorrida() {
  if (pressionadas.has('galopar')) pressionadas.delete('galopar');
  else pressionadas.add('galopar');
  avisar();
}

function indiceTecla(): Map<string, Acao[]> {
  const idx = new Map<string, Acao[]>();
  for (const acao of ACOES) {
    if (acao === 'fechar') continue;
    for (const tecla of teclasDe(acao)) {
      const lista = idx.get(tecla);
      if (lista) lista.push(acao);
      else idx.set(tecla, [acao]);
    }
  }
  return idx;
}

export function emConflito(acao: Acao): boolean {
  if (acao === 'fechar' || acao === 'limparAlvo') return false;
  const idx = indiceTecla();
  return teclasDe(acao).some(t => (idx.get(t)?.length ?? 0) > 1);
}

export function conflitoCom(acao: Acao): Acao | null {
  if (!emConflito(acao)) return null;
  const idx = indiceTecla();
  for (const t of teclasDe(acao)) {
    const lista = idx.get(t) ?? [];
    const outra = lista.find(a => a !== acao);
    if (outra) return outra;
  }
  return null;
}

export function ligarAcao(acao: Acao, fn: () => void): () => void {
  ligarOuvinte();
  toques.set(acao, fn);
  return () => { if (toques.get(acao) === fn) toques.delete(acao); };
}

export function empilharFechar(camada: CamadaFechar): () => void {
  ligarOuvinte();
  pilha.push(camada);
  return () => {
    const i = pilha.lastIndexOf(camada);
    if (i >= 0) pilha.splice(i, 1);
  };
}

export function registrarMira(proxima: MiraTeclado): () => void {
  ligarOuvinte();
  mira = proxima;
  return () => { if (mira === proxima) mira = null; };
}

export function aoComecarMovimento(fn: () => void): () => void {
  aoAndar = fn;
  return () => { if (aoAndar === fn) aoAndar = null; };
}

export function iniciarCaptura(acao: Acao, avisarCaptura: (r: ResultadoCaptura) => void): () => void {
  ligarOuvinte();
  captura = { acao, avisar: avisarCaptura };
  avisar();
  return () => {
    if (captura?.avisar === avisarCaptura) {
      captura = null;
      avisar();
    }
  };
}

export function capturando(): Acao | null {
  return captura?.acao ?? null;
}

function modificadorPuro(tecla: string): boolean {
  return tecla === 'Shift' || tecla === 'Control' || tecla === 'Alt' || tecla === 'Meta';
}

function nomeMod(tecla: string): string {
  if (tecla === 'Control') return 'Ctrl';
  return tecla;
}

function modOcupado(mod: string): boolean {
  for (const acao of ACOES) {
    if (mapa[acao] === mod) return true;
  }
  return false;
}

function combinacaoUsaMantida(e: KeyboardEvent, tecla: string): boolean {
  for (const acao of ACOES) {
    const bind = mapa[acao];
    if (!modificadorPuro(bind)) continue;
    if (tecla === bind) continue;
    if (bind === 'Shift' && e.shiftKey && !e.code.startsWith('Shift')) return true;
    if (bind === 'Control' && e.ctrlKey && !e.code.startsWith('Control')) return true;
    if (bind === 'Alt' && e.altKey && !e.code.startsWith('Alt')) return true;
    if (bind === 'Meta' && e.metaKey && !e.code.startsWith('Meta')) return true;
    const mod = nomeMod(bind);
    if (tecla.split('+').includes(mod)) return true;
  }
  return false;
}

function teclaDoEvento(e: KeyboardEvent): string {
  const code = e.code;
  if ((code === 'ShiftLeft' || code === 'ShiftRight') && !e.ctrlKey && !e.altKey && !e.metaKey) return 'Shift';
  if ((code === 'ControlLeft' || code === 'ControlRight') && !e.altKey && !e.metaKey && !e.shiftKey) return 'Control';
  if ((code === 'AltLeft' || code === 'AltRight') && !e.ctrlKey && !e.metaKey && !e.shiftKey) return 'Alt';
  if ((code === 'MetaLeft' || code === 'MetaRight') && !e.ctrlKey && !e.altKey && !e.shiftKey) return 'Meta';

  const partes: string[] = [];
  if (e.ctrlKey) partes.push('Ctrl');
  if (e.altKey) partes.push('Alt');
  const shiftLivre = !modOcupado('Shift');
  if (e.shiftKey && shiftLivre) partes.push('Shift');
  if (e.metaKey) partes.push('Meta');
  partes.push(code);
  return partes.join('+');
}

function focoEmTexto(e: KeyboardEvent): boolean {
  const alvo = e.target;
  if (!(alvo instanceof HTMLElement)) return false;
  return !!alvo.closest('input, textarea, [contenteditable="true"], [contenteditable=""]');
}

function resolverEscape() {
  if (mira?.ativa()) {
    mira.cancelar();
    return;
  }
  const topo = pilha.at(-1);
  if (topo) {
    topo.fechar();
    return;
  }
  toques.get('limparAlvo')?.();
}

function modalAberto(): CamadaFechar | null {
  for (let i = pilha.length - 1; i >= 0; i--) {
    if (pilha[i].modal) return pilha[i];
  }
  return null;
}

function tratarCaptura(e: KeyboardEvent) {
  const atual = captura;
  if (!atual) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  if (e.repeat) return;
  if (e.code === 'Escape' && !e.ctrlKey && !e.altKey && !e.metaKey) {
    atual.avisar({ ok: false, motivo: 'cancelou' });
    return;
  }
  const tecla = teclaDoEvento(e);
  if (combinacaoUsaMantida(e, tecla)) {
    atual.avisar({ ok: false, motivo: 'modificador' });
    return;
  }
  if (NAVEGADOR.has(tecla) || teclaDoChrome(e)) {
    atual.avisar({ ok: false, motivo: 'navegador' });
    return;
  }
  atual.avisar({ ok: true, tecla });
}

function onKeyDown(e: KeyboardEvent) {
  // Esc com um item na mão só cancela o arraste (input/arraste.ts); a janela fica aberta.
  if (e.code === 'Escape' && arrastandoItem()) return;
  if (captura) {
    tratarCaptura(e);
    return;
  }
  if (focoEmTexto(e)) {
    if (noMundo && ehTeclaFuncao(e.code)) engolirChrome(e);
    if (e.code === 'Escape' && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      if (!e.repeat) {
        const topo = pilha.at(-1);
        if (topo) topo.fechar();
      }
    }
    return;
  }

  if (noMundo && teclaDoChrome(e)) engolirChrome(e);

  if (e.code === 'Escape' && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) {
    e.preventDefault();
    if (!e.repeat) resolverEscape();
    return;
  }

  const tecla = teclaDoEvento(e);
  const idx = indiceTecla();
  const acoes = idx.get(tecla);
  if (!acoes || acoes.length === 0) return;

  const vivas = acoes.filter(a => !emConflito(a));
  if (vivas.length === 0) {
    e.preventDefault();
    return;
  }

  const modal = modalAberto();
  for (const acao of vivas) {
    if (acao === 'limparAlvo') continue;
    if (ALTERNADAS.has(acao)) {
      if (e.repeat) { e.preventDefault(); continue; }
      if (modal && !modal.deixaPassar.has(acao)) continue;
      if (pressionadas.has(acao)) pressionadas.delete(acao);
      else pressionadas.add(acao);
      e.preventDefault();
      avisar();
      continue;
    }
    if (SEGURADAS.has(acao)) {
      // Vale com janela aberta, como as teclas da barra que ela acompanha. A soltura limpa no onKeyUp.
      pressionadas.add(acao);
      e.preventDefault();
      continue;
    }
    if (MANTIDAS.has(acao)) {
      if (!aoAndar) continue;
      if (modal && !modal.deixaPassar.has(acao)) continue;
      if (e.repeat && pressionadas.has(acao)) {
        e.preventDefault();
        continue;
      }
      const nova = !pressionadas.has(acao);
      pressionadas.add(acao);
      e.preventDefault();
      if (nova) aoAndar?.();
      continue;
    }
    if (e.repeat) {
      // Tecla da barra segurada: a repetição não chega ao botão focado — senão
      // Espaço segurado "clica" o que tiver foco (linha do grimório, slot).
      if (daBarra(acao)) { e.preventDefault(); e.stopPropagation(); }
      continue;
    }
    if (modal && !modal.deixaPassar.has(acao) && !daBarra(acao)) continue;
    if (SO_NO_MUNDO.has(acao) && !focoNoMundo(e)) continue;
    const fn = toques.get(acao);
    if (!fn) continue;
    e.preventDefault();
    e.stopPropagation();
    fn();
  }
}

function onKeyUp(e: KeyboardEvent) {
  const tecla = teclaDoEvento(e);
  const code = teclaDoCode(e.code);
  for (const acao of [...pressionadas]) {
    if (ALTERNADAS.has(acao)) continue;
    if (teclasDe(acao).includes(tecla) || teclasDe(acao).includes(code)) {
      pressionadas.delete(acao);
    }
  }
}

function teclaDoCode(code: string): string {
  if (code === 'ShiftLeft' || code === 'ShiftRight') return 'Shift';
  if (code === 'ControlLeft' || code === 'ControlRight') return 'Control';
  if (code === 'AltLeft' || code === 'AltRight') return 'Alt';
  if (code === 'MetaLeft' || code === 'MetaRight') return 'Meta';
  return code;
}

function onBlur() {
  for (const acao of [...pressionadas]) {
    if (!ALTERNADAS.has(acao)) pressionadas.delete(acao);
  }
}

function onContextMenu(e: MouseEvent) {
  if (!noMundo) return;
  const alvo = e.target;
  if (alvo instanceof Element && alvo.closest('input, textarea, [contenteditable="true"], [contenteditable=""]')) return;
  e.preventDefault();
}

export function ligarOuvinte() {
  if (ligado || typeof window === 'undefined') return;
  ligado = true;
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('keyup', onKeyUp, true);
  window.addEventListener('contextmenu', onContextMenu, true);
  window.addEventListener('blur', onBlur);
  void carregarLayout();
}

async function carregarLayout() {
  try {
    const teclado = (navigator as Navigator & {
      keyboard?: { getLayoutMap(): Promise<{ get(code: string): string | undefined }> };
    }).keyboard;
    if (!teclado?.getLayoutMap) return;
    layout = await teclado.getLayoutMap();
    avisar();
  } catch {
    /* sem Keyboard API — rótulo sai do code */
  }
}

/*
  Nomes longos viram abreviação: o rótulo cabe embaixo de um quadrado de 32 px na barra.
  O teclado numérico vem antes do layout: o mapa do navegador não traz o Numpad, e "Numpad1"
  não cabe.
*/
const NUMPAD: Readonly<Record<string, string>> = {
  NumpadAdd: 'Num+', NumpadSubtract: 'Num-', NumpadMultiply: 'Num*', NumpadDivide: 'Num/',
  NumpadDecimal: 'Num.', NumpadComma: 'Num,', NumpadEnter: 'NumEnt', NumpadEqual: 'Num='
};
const ABREVIADAS: Readonly<Record<string, string>> = {
  Backspace: 'Bksp', Delete: 'Del', Insert: 'Ins', PageUp: 'PgUp', PageDown: 'PgDn',
  CapsLock: 'Caps', NumLock: 'NumLk', ScrollLock: 'ScrLk', PrintScreen: 'PrtSc',
  ContextMenu: 'Menu', Enter: 'Enter', Home: 'Home', End: 'End', Pause: 'Pause'
};

function rotuloDoCode(code: string): string {
  if (/^Numpad\d$/.test(code)) return `Num${code.slice(6)}`;
  if (NUMPAD[code]) return NUMPAD[code];
  if (ABREVIADAS[code]) return ABREVIADAS[code];
  const impresso = layout?.get(code);
  if (impresso) {
    if (impresso.length === 1) return /[a-z]/.test(impresso) ? impresso.toUpperCase() : impresso;
    return impresso;
  }
  if (code.startsWith('Key') && code.length === 4) return code.slice(3);
  if (code.startsWith('Digit') && code.length === 6) return code.slice(5);
  if (code.startsWith('F') && /^F\d{1,2}$/.test(code)) return code;
  if (code === 'ArrowUp') return '↑';
  if (code === 'ArrowDown') return '↓';
  if (code === 'ArrowLeft') return '←';
  if (code === 'ArrowRight') return '→';
  if (code === 'Escape') return 'Esc';
  if (code === 'Tab') return 'Tab';
  if (code === 'Space') return 'Espaço';
  if (code === 'Minus') return '-';
  if (code === 'Equal') return '=';
  if (code === 'BracketLeft') return '[';
  if (code === 'BracketRight') return ']';
  if (code === 'Backslash') return '\\';
  if (code === 'Semicolon') return ';';
  if (code === 'Quote') return "'";
  if (code === 'Comma') return ',';
  if (code === 'Period') return '.';
  if (code === 'Slash') return '/';
  if (code === 'Backquote') return '`';
  return code;
}

export function rotuloTecla(tecla: string): string {
  if (tecla === 'Shift' || tecla === 'Control' || tecla === 'Alt' || tecla === 'Meta') {
    return tecla === 'Control' ? 'Ctrl' : tecla;
  }
  const partes = tecla.split('+');
  const code = partes.pop() ?? tecla;
  const mods = partes.map(p => p === 'Control' ? 'Ctrl' : p);
  return [...mods, rotuloDoCode(code)].join('+');
}

/** Tecla sozinha (Shift, Control…) mantém o nome; em combinação, vira símbolo. */
const MOD_CURTO: Readonly<Record<string, string>> = { Shift: '⇧', Ctrl: '^', Control: '^', Alt: 'Alt', Meta: '⌘' };

/**
 * Rótulo para espaço apertado (embaixo do quadrado da barra): modificador vira símbolo e não
 * leva "+". Ex.: Shift+Numpad1 → "⇧Num1", Ctrl+KeyQ → "^Q". O nome inteiro vai no title.
 */
export function rotuloCurto(tecla: string): string {
  if (tecla === 'Shift' || tecla === 'Control' || tecla === 'Alt' || tecla === 'Meta') return rotuloTecla(tecla);
  const partes = tecla.split('+');
  const code = partes.pop() ?? tecla;
  const mods = partes.map(p => MOD_CURTO[p] ?? p);
  return mods.join('') + rotuloDoCode(code);
}

